"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.classifyOrphanEventScores = exports.eventScoreHasStartedPlay = exports.liveScoringRoundStarted = void 0;
exports.buildEventScores = buildEventScores;
exports.buildFirstTeeEventScores = buildFirstTeeEventScores;
exports.applyTeeSetToExistingHoleScores = applyTeeSetToExistingHoleScores;
const handicapCalcs_1 = require("./handicapCalcs");
const resolvedStartHole_1 = require("./resolvedStartHole");
const shotgunSlotUtils_1 = require("./shotgunSlotUtils");
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const toPositiveInt = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0)
        return null;
    return Math.round(parsed);
};
const resolveRoundTotalHoles = (viewEvent, viewEventData, roundNumber, teeSetName) => {
    const rounds = viewEvent.rounds;
    const roundConfig = rounds?.[roundNumber - 1];
    const configuredTotalHoles = toPositiveInt(roundConfig?.totalHoles);
    if (configuredTotalHoles)
        return configuredTotalHoles;
    const course = viewEventData.courseData.find((c) => c._id === roundConfig?.courseID);
    const teeSetMatch = course?.teeSets.find((ts) => ts.teeSet === teeSetName || ts.teeName === teeSetName);
    const teeSetHoleCount = toPositiveInt((teeSetMatch?.holes ?? course?.teeSets?.[0]?.holes)?.length);
    if (teeSetHoleCount)
        return teeSetHoleCount;
    const eventTotal = toPositiveInt(viewEvent.totalHoles);
    const totalRounds = toPositiveInt(viewEvent.totalRounds);
    if (eventTotal && totalRounds) {
        const perRound = eventTotal / totalRounds;
        if (Number.isFinite(perRound) && perRound > 0)
            return Math.round(perRound);
    }
    return 18;
};
const getMarkerScoreCode = (viewEvent, registrations, teeTime, roundNumber, playerScoreCode) => {
    const scoreType = (viewEvent.scoreType ?? '').toLowerCase();
    if (scoreType === 'opponent') {
        const roundConfig = viewEvent.rounds?.[roundNumber - 1];
        const teeTimeRegs = registrations.filter((reg) => {
            if ((0, shotgunSlotUtils_1.isShotgunRound)(roundConfig)) {
                const startingHole = Number(teeTime.startingHole);
                if (!Number.isFinite(startingHole) || startingHole <= 0)
                    return false;
                const slotOrder = (0, shotgunSlotUtils_1.normalizeShotgunOrder)(teeTime.shotgunOrder);
                return (0, shotgunSlotUtils_1.registrationInShotgunSlot)(reg, roundNumber, Math.round(startingHole), slotOrder);
            }
            return reg.teeTimes?.some((tt) => tt.teeTime === teeTime.teeTime && tt.round === roundNumber);
        });
        const uniqueScoreCodes = Array.from(new Set(teeTimeRegs.map((r) => r.scoreCode)));
        const dup = [...uniqueScoreCodes];
        const markerCodes = [dup.pop(), ...dup];
        return markerCodes[uniqueScoreCodes.indexOf(playerScoreCode)] ?? '';
    }
    if (scoreType === 'self')
        return playerScoreCode;
    return '';
};
const getPlayerStrokes = (viewEvent, viewEventData, registrations, scoreCode, markerScoreCode, roundNumber) => {
    const round = viewEvent.rounds?.[roundNumber - 1];
    if (!round)
        return { playerStrokes: 0, markerStrokes: 0 };
    const scoreFormat = String(round.scoreFormat ?? viewEvent.scoreFormat ?? 'gross').toLowerCase();
    const hdcpUsageEntry = Array.isArray(round.hdcpUsage) ? round.hdcpUsage[0] : round.hdcpUsage;
    const hdcpType = hdcpUsageEntry?.type ? String(hdcpUsageEntry.type).toLowerCase() : undefined;
    if (scoreFormat !== 'gross' && hdcpType) {
        const map = {
            straight: 'straightStrokes',
            adjusted: 'adjustedStrokes',
            composite: 'compositeStrokes',
        };
        const fnKey = map[hdcpType];
        if (fnKey) {
            return (0, handicapCalcs_1.HandicapCalcs)(fnKey, {
                round,
                viewEventData,
                registrations,
                scoreCode,
                markerScoreCode,
            });
        }
    }
    return { playerStrokes: 0, markerStrokes: 0 };
};
// ---------------------------------------------------------------------------
// buildEventScores — full tee-sheet builder (replaces CreateEventScores.tsx)
// ---------------------------------------------------------------------------
function buildEventScores({ auth, viewEvent, viewEventData }) {
    const userID = auth?.user?.profile?.sub ?? '';
    const newDate = new Date();
    const registrations = viewEventData.registrations.filter((reg) => !reg.canceled && !reg.withdrawn && !reg.disqualified && !reg.refunded);
    const filteredRegistrations = registrations.filter((reg, index, self) => index === self.findIndex((r) => r.scoreCode === reg.scoreCode));
    const eventScores = [];
    filteredRegistrations.forEach((registration) => {
        const playerScores = [];
        (registration.teeTimes ?? []).forEach((teeTime) => {
            const roundConfig = viewEvent.rounds[teeTime.round - 1];
            const totalHoles = resolveRoundTotalHoles(viewEvent, viewEventData, teeTime.round, teeTime.teeSet);
            const course = viewEventData.courseData.find((c) => c._id === roundConfig?.courseID);
            const teeSetMatch = course?.teeSets.find((ts) => ts.teeSet === teeTime.teeSet || ts.teeName === teeTime.teeSet);
            const selectedHoles = teeSetMatch?.holes ?? course?.teeSets?.[0]?.holes ?? [];
            const markerScoreCode = getMarkerScoreCode(viewEvent, registrations, teeTime, teeTime.round, registration.scoreCode);
            const strokes = getPlayerStrokes(viewEvent, viewEventData, registrations, registration.scoreCode, markerScoreCode, teeTime.round);
            const fromTee = teeTime.startingHole;
            const resolvedFromRound = roundConfig?.shotgunStart || roundConfig?.splitTees
                ? 1
                : (0, resolvedStartHole_1.resolvedStartHole)(roundConfig);
            const startingHoleForScore = fromTee != null && Number(fromTee) > 0 && Number.isFinite(Number(fromTee))
                ? Math.round(Number(fromTee))
                : resolvedFromRound;
            const holeHdcpInputs = Array.from({ length: totalHoles }, (_, i) => ({
                holeNumber: i + 1,
                hdcp: selectedHoles?.[i]?.hdcp ?? 0,
            }));
            const playerStrokeAllocs = (0, handicapCalcs_1.perHoleStrokeAllocations)(strokes.playerStrokes, holeHdcpInputs);
            const markerStrokeAllocs = (0, handicapCalcs_1.perHoleStrokeAllocations)(strokes.markerStrokes, holeHdcpInputs);
            playerScores.push({
                roundNumber: teeTime.round,
                matchNumber: teeTime?.matchNumber ?? null,
                roundDate: viewEvent.rounds[teeTime.round - 1].roundDate,
                teeTime: teeTime.teeTime,
                playerScoreCode: registration.scoreCode,
                markerScoreCode,
                startingHole: startingHoleForScore,
                shotgunOrder: teeTime?.shotgunOrder ?? null,
                holeScores: Array.from({ length: totalHoles }, (_, i) => {
                    const holeData = selectedHoles?.[i] ?? { par: 4, hdcp: 0 };
                    return {
                        holeNumber: i + 1,
                        par: holeData.par,
                        playerScore: 0,
                        markerScore: 0,
                        playerHcpAdj: playerStrokeAllocs[i] ?? 0,
                        markerHcpAdj: markerStrokeAllocs[i] ?? 0,
                    };
                }),
                totalScore: 0,
                matchScore: null,
                matchHole: null,
                challengeSubmissions: [],
                sideGames: [],
                indPlayoffWin: false,
                teamPlayoffWin: false,
                withdrawn: false,
                disqualified: false,
                submitted: false,
                submittedAt: null,
                submittedBy: null,
                confirmed: false,
                confirmedAt: null,
                confirmedBy: null,
                createdAt: newDate,
                createdBy: userID,
                modifiedAt: newDate,
                modifiedBy: userID,
            });
        });
        if (playerScores.length === 0) {
            console.warn('[buildEventScores] no rounds for registration', {
                playerName: registration.playerName,
                scoreCode: registration.scoreCode,
            });
            return;
        }
        eventScores.push({
            eventID: viewEvent._id,
            playerScoreCode: playerScores[0].playerScoreCode,
            indPlayoffWin: false,
            teamPlayoffWin: false,
            finalRank: { gross: 0, net: 0 },
            finalTeamRank: { gross: 0, net: 0 },
            withdrawn: false,
            disqualified: false,
            roundScores: playerScores,
            createdAt: newDate,
            createdBy: userID,
            modifiedAt: newDate,
            modifiedBy: userID,
        });
    });
    return eventScores;
}
// ---------------------------------------------------------------------------
// buildFirstTeeEventScores — first-tee self-selection builder
// ---------------------------------------------------------------------------
function buildFirstTeeEventScores({ selectedPlayers, roundNumber, teeTime, startingHole, eventData, courseData, auth, markerScoreCode: markerScoreCodeParam, }) {
    const userID = auth?.user?.profile?.sub ?? '';
    const newDate = new Date();
    const roundConfig = eventData?.rounds?.[roundNumber - 1];
    const passedHole = Number(startingHole);
    const effectiveStartingHole = Number.isFinite(passedHole) && passedHole > 0
        ? Math.round(passedHole)
        : (0, resolvedStartHole_1.resolvedStartHole)(roundConfig);
    const scoreFormat = String(roundConfig?.scoreFormat ?? eventData?.scoreFormat ?? 'gross').toLowerCase();
    const hdcpUsageEntry = Array.isArray(roundConfig?.hdcpUsage)
        ? roundConfig.hdcpUsage[0]
        : roundConfig?.hdcpUsage;
    const hdcpType = hdcpUsageEntry?.type?.toLowerCase();
    const shouldCalcHdcp = scoreFormat !== 'gross' && !!hdcpType && hdcpType === 'straight';
    const resolveHoles = (teeSetName) => {
        const match = (courseData?.teeSets ?? []).find((ts) => (ts.teeName ?? ts.teeSet) === teeSetName);
        return match?.holes ?? courseData?.teeSets?.[0]?.holes ?? [];
    };
    const resolveTotalHoles = (teeSetName) => {
        const fromRound = Number(roundConfig?.totalHoles);
        if (fromRound > 0)
            return Math.round(fromRound);
        const holes = resolveHoles(teeSetName);
        if (holes.length > 0)
            return holes.length;
        const eventTotal = Number(eventData?.totalHoles);
        const totalRounds = Number(eventData?.totalRounds);
        if (eventTotal > 0 && totalRounds > 0) {
            const perRound = eventTotal / totalRounds;
            if (Number.isFinite(perRound) && perRound > 0)
                return Math.round(perRound);
        }
        return 18;
    };
    const calcPlayerStrokes = (player, teeSetData, totalHoles) => {
        if (!shouldCalcHdcp || !teeSetData)
            return 0;
        const gender = player.gender || 'mens';
        const slope = teeSetData[`${gender}Slope`];
        const rating = teeSetData[`${gender}Rating`];
        const par = teeSetData.holes.reduce((s, h) => s + (Number(h.par) || 4), 0);
        return (Math.round((player.playerHandicap ?? 0) *
            (totalHoles / 18) *
            ((slope ?? 113) / 113) +
            (rating ?? 0) -
            par) || 0);
    };
    return selectedPlayers.map((player) => {
        const holes = resolveHoles(player.teeSet);
        const totalHoles = resolveTotalHoles(player.teeSet);
        const teeSetData = (courseData?.teeSets ?? []).find((ts) => (ts.teeName ?? ts.teeSet) === player.teeSet) ?? courseData?.teeSets?.[0];
        const playerStrokes = calcPlayerStrokes(player, teeSetData, totalHoles);
        const holeHdcpInputs = Array.from({ length: totalHoles }, (_, i) => ({
            holeNumber: i + 1,
            hdcp: holes[i]?.hdcp ?? 0,
        }));
        const playerStrokeAllocs = (0, handicapCalcs_1.perHoleStrokeAllocations)(playerStrokes, holeHdcpInputs);
        const holeScores = Array.from({ length: totalHoles }, (_, i) => {
            const holeData = holes[i] ?? { par: 4, hdcp: 0 };
            return {
                holeNumber: i + 1,
                par: Number(holeData.par) || 4,
                playerScore: 0,
                markerScore: 0,
                playerHcpAdj: playerStrokeAllocs[i] ?? 0,
                markerHcpAdj: 0,
            };
        });
        const roundScore = {
            roundNumber,
            matchNumber: null,
            roundDate: roundConfig?.roundDate ?? null,
            teeTime,
            playerScoreCode: player.scoreCode,
            markerScoreCode: markerScoreCodeParam ?? '',
            startingHole: effectiveStartingHole,
            shotgunOrder: null,
            holeScores,
            totalScore: 0,
            matchScore: null,
            matchHole: null,
            challengeSubmissions: [],
            sideGames: [],
            indPlayoffWin: false,
            teamPlayoffWin: false,
            withdrawn: false,
            disqualified: false,
            submitted: false,
            submittedAt: null,
            submittedBy: null,
            confirmed: false,
            confirmedAt: null,
            confirmedBy: null,
            createdAt: newDate,
            createdBy: userID,
            modifiedAt: newDate,
            modifiedBy: userID,
        };
        return {
            eventID: eventData?._id ?? '',
            playerScoreCode: player.scoreCode,
            indPlayoffWin: false,
            teamPlayoffWin: false,
            finalRank: { gross: 0, net: 0 },
            finalTeamRank: { gross: 0, net: 0 },
            withdrawn: false,
            disqualified: false,
            roundScores: [roundScore],
            createdAt: newDate,
            createdBy: userID,
            modifiedAt: newDate,
            modifiedBy: userID,
        };
    });
}
/**
 * Recompute par and playerHcpAdj from the chosen tee set and straight handicap
 * rules (same as buildFirstTeeEventScores). Preserves playerScore, markerScore, markerHcpAdj.
 */
function applyTeeSetToExistingHoleScores({ existingHoleScores, roundNumber, eventData, courseData, teeSet, playerHandicap = null, gender = 'mens', }) {
    const roundConfig = eventData?.rounds?.[roundNumber - 1];
    const scoreFormat = String(roundConfig?.scoreFormat ?? eventData?.scoreFormat ?? 'gross').toLowerCase();
    const hdcpUsageEntry = Array.isArray(roundConfig?.hdcpUsage)
        ? roundConfig.hdcpUsage[0]
        : roundConfig?.hdcpUsage;
    const hdcpType = hdcpUsageEntry?.type?.toLowerCase();
    const shouldCalcHdcp = scoreFormat !== 'gross' && !!hdcpType && hdcpType === 'straight';
    const resolveHoles = (teeSetName) => {
        const match = (courseData?.teeSets ?? []).find((ts) => (ts.teeName ?? ts.teeSet) === teeSetName);
        return match?.holes ?? courseData?.teeSets?.[0]?.holes ?? [];
    };
    const holes = resolveHoles(teeSet);
    const totalHoles = existingHoleScores.length;
    const teeSetData = (courseData?.teeSets ?? []).find((ts) => (ts.teeName ?? ts.teeSet) === teeSet) ??
        courseData?.teeSets?.[0];
    const calcPlayerStrokes = () => {
        if (!shouldCalcHdcp || !teeSetData)
            return 0;
        const g = gender || 'mens';
        const slope = teeSetData[`${g}Slope`];
        const rating = teeSetData[`${g}Rating`];
        const par = teeSetData.holes.reduce((s, h) => s + (Number(h.par) || 4), 0);
        return (Math.round((playerHandicap ?? 0) *
            (totalHoles / 18) *
            ((slope ?? 113) / 113) +
            (rating ?? 0) -
            par) || 0);
    };
    const playerStrokes = calcPlayerStrokes();
    const holeHdcpInputs = existingHoleScores.map((existing, i) => {
        const hn = Number(existing?.holeNumber);
        return {
            holeNumber: Number.isFinite(hn) && hn > 0 ? Math.round(hn) : i + 1,
            hdcp: holes[i]?.hdcp ?? 0,
        };
    });
    const playerStrokeAllocs = (0, handicapCalcs_1.perHoleStrokeAllocations)(playerStrokes, holeHdcpInputs);
    return existingHoleScores.map((existing, i) => {
        const holeData = holes[i] ?? { par: 4, hdcp: 0 };
        return {
            ...existing,
            holeNumber: existing?.holeNumber ?? i + 1,
            par: Number(holeData.par) || 4,
            playerHcpAdj: playerStrokeAllocs[i] ?? 0,
        };
    });
}
/**
 * True if the round should be treated as started for first-tee / tee-set flows.
 * Aligns with CreateEventScores merge guard; for opponent scoring, markerScore > 0 also locks.
 */
var eventScoreStartedPlay_1 = require("./eventScoreStartedPlay");
Object.defineProperty(exports, "liveScoringRoundStarted", { enumerable: true, get: function () { return eventScoreStartedPlay_1.liveScoringRoundStarted; } });
Object.defineProperty(exports, "eventScoreHasStartedPlay", { enumerable: true, get: function () { return eventScoreStartedPlay_1.eventScoreHasStartedPlay; } });
Object.defineProperty(exports, "classifyOrphanEventScores", { enumerable: true, get: function () { return eventScoreStartedPlay_1.classifyOrphanEventScores; } });
